// projected-submission-builder.js
// Unified projection strategy implementation (v1.0)
// - Traverse persisted submission snapshot ONLY
// - Maintain path[] stack for nested repeaters
// - Emit normalized facts
// - Derive UI tree nodes + export rows from facts

'use strict';

const _ = require('../../../lodash');
const getConstants = require('../../../utils/getConstants');
const constants = getConstants();
const { formItemTypes } = constants;

/**
 * OpsPerfect item type IDs (based on your uploaded examples)
 * Adjust if your constants differ.
 */
const ItemTypes = Object.freeze({
   TODO: formItemTypes.TODO.id,
   OPTIONS: formItemTypes.OPTIONS.id,
   NUMBER: formItemTypes.NUMBER.id,
   TEXT: formItemTypes.TEXT.id,
   DATETIME: formItemTypes.DATETIME.id,
   PHOTO: formItemTypes.PHOTO.id,
   SECTION: formItemTypes.SECTION.id,
   REPEATER: formItemTypes.REPEATER.id,
   REPEATER_GROUP: formItemTypes.REPEATER_GROUP.id,
   CALCULATED: formItemTypes.CALCULATED.id
});

/**
 * Build fast lookup for form items by id.
 */
function indexFormItems(form) {
   const byId = new Map();
   for (const it of form?.items ?? []) byId.set(it.id, it);
   return byId;
}

/**
 * Build children adjacency for submission snapshot items by idParent.
 */
function indexSubmissionTree(submission) {
   const items = submission?.items ?? [];
   const byId = new Map(items.map((i) => [i.id, i]));
   const childrenByParent = new Map();

   for (const it of items) {
      const pid = it.idParent ?? null;
      if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
      childrenByParent.get(pid).push(it);
   }

   // Stable traversal order: by 'no' then 'upd'
   for (const kids of childrenByParent.values()) {
      kids.sort((a, b) => {
         const an = a.no ?? 0;
         const bn = b.no ?? 0;
         if (an !== bn) return an - bn;
         return String(a.upd ?? '').localeCompare(String(b.upd ?? ''));
      });
   }

   return { byId, childrenByParent };
}

function isRepeater(item) {
   return item?.idItemType === ItemTypes.REPEATER;
}
function isRepeaterGroup(item) {
   return item?.idItemType === ItemTypes.REPEATER_GROUP;
}
function isSection(item) {
   return item?.idItemType === ItemTypes.SECTION;
}

/**
 * Should this submission entry emit a fact?
 * - Containers (SECTION/REPEATER/REPEATER_GROUP) never emit
 * - Otherwise emit if includeNulls or has response payload
 */
function shouldEmitFact(item, includeNulls) {
   if (!item) return false;
   if (isRepeater(item) || isRepeaterGroup(item) || isSection(item)) return false;

   const hasResponse = item.response != null && (!Array.isArray(item.response) || item.response.length > 0);

   return includeNulls ? true : hasResponse;
}

/**
 * For multi-response fields, use latest upd; otherwise prefer response.upd then item.upd.
 */
function getResponseUpd(item) {
   if (!item) return null;

   if (Array.isArray(item.response)) {
      const upds = item.response
         .map((r) => r?.upd)
         .filter(Boolean)
         .sort();
      return upds.length ? upds[upds.length - 1] : item.upd ?? null;
   }

   return item.response?.upd ?? item.upd ?? null;
}

/**
 * Parse persisted values ONLY (no lookups).
 *
 * IMPORTANT:
 * - OPTIONS may be single or multiple based on formItem.options.allowMultiple
 * - PHOTO can be multiple; always return array of payload objects/strings
 * - TODO is scalar boolean
 * - Others are scalar first.val
 */
function parsePersistedValue(itemType, response, formItem) {
   if (response == null) return null;

   const arr = Array.isArray(response) ? response : [response];

   // TODO: { val: true/false }
   if (itemType === ItemTypes.TODO) {
      const first = arr[0];
      if (!first) return null;
      const v = first.val;
      if (v == null) return null;
      return Boolean(v);
   }

   // OPTIONS: [{ val: "<id>", txt: "<label>", upd }, ...]
   if (itemType === ItemTypes.OPTIONS) {
      const options = arr
         .filter(Boolean)
         .map((r) => ({ id: r.val ?? null, txt: r.txt ?? null }))
         .filter((o) => o.id != null || o.txt != null);

      const allowMultiple = Boolean(formItem?.options?.allowMultiple);

      if (allowMultiple) {
         // Multi-select: array
         return options;
      }

      // Single-select: scalar option object (or null)
      return options[0] ?? null;
   }

   // PHOTO: [{ val: { localUri, remoteUri, mimeType, id }, upd }, ...]
   if (itemType === ItemTypes.PHOTO) {
      const photos = arr
         .filter(Boolean)
         .map((r) => r.val ?? null)
         .filter((v) => v != null)
         .map((v) => {
            if (typeof v === 'object') return v;
            if (typeof v === 'string') {
               try {
                  return JSON.parse(v);
               } catch {
                  return v; // fail-soft
               }
            }
            return v;
         });

      return photos.length ? photos : null;
   }

   // Default scalar: take first val
   const first = arr[0];
   if (!first) return null;
   return first.val ?? null;
}

/**
 * Build ProjectedSubmission:
 * {
 *   meta: {...},
 *   facts: [...],
 *   nodes: [...]
 * }
 */
// Describe historical repeater fields independently of whether answer rows exist.
// Nested questions belong only to the closest enclosing repeater.
function getRepeaterColumns(form) {
   const items = form?.items ?? [];
   const byId = new Map(items.map((item) => [item.id, item]));
   const columns = Object.fromEntries(items.filter(isRepeater).map((item) => [item.id, []]));
   for (const item of items) {
      if (isRepeater(item) || isRepeaterGroup(item) || isSection(item)) continue;
      const visited = new Set([item.id]);
      let parentId = item.idParent;
      while (parentId != null && !visited.has(parentId)) {
         visited.add(parentId);
         const parent = byId.get(parentId);
         if (!parent) break;
         if (isRepeater(parent)) {
            columns[parent.id].push({
               id: item.id,
               label: item.label ?? String(item.id),
               idItemType: item.idItemType,
               allowMultiple: Boolean(item.options?.allowMultiple)
            });
            break;
         }
         parentId = parent.idParent;
      }
   }
   return columns;
}

function buildProjectedSubmission({ submission, form, includeNulls = false, includeGroupsWithoutRowKey = false }) {
   if (!submission) throw new Error('submission is required');
   if (!form) throw new Error('form is required');

   const formItemsById = indexFormItems(form);
   const { childrenByParent } = indexSubmissionTree(submission);

   const projected = {
      meta: {
         noSubmission: submission.noSubmission ?? submission.no ?? null,
         noForm: submission.form?.noForm ?? form.noForm ?? null,
         noLocation: submission.noLocation ?? null,
         ctd: submission.ctd ?? null,
         upd: submission.upd ?? null
      },
      facts: [],
      nodes: [],
      // Actual selected groups can exist with zero answered leaf questions.
      // These are opt-in to exporters so existing Google Sheets row contracts stay stable.
      groupPaths: [],
      invalidRepeaterGroups: [],
      // Schema declarations come from the original form, even when no response items exist.
      repeaterFormItems: (form.items ?? []).filter((item) => item.idItemType === ItemTypes.REPEATER).map((item) => item.id),
      repeaterColumns: getRepeaterColumns(form)
   };

   const pathStack = [];

   function emitFact(item) {
      const formItem = formItemsById.get(item.idFormItem);

      const label = formItem?.label ?? item.label ?? null;
      const value = parsePersistedValue(item.idItemType, item.response, formItem);
      if (!includeNulls && value == null) return;

      projected.facts.push({
         key: `formItem:${item.idFormItem}`,
         itemType: item.idItemType,
         label,
         path: pathStack.slice(),
         value,
         upd: getResponseUpd(item),

         // Debug/ties
         noItem: item.no ?? null,
         idSubmissionItem: item.id ?? null,
         idFormItem: item.idFormItem ?? null
      });
   }

   function traverse(parentId) {
      const children = childrenByParent.get(parentId) ?? [];

      for (const child of children) {
         if (isSection(child)) {
            // Structural grouping only; traverse children
            traverse(child.id);
            continue;
         }

         if (isRepeater(child)) {
            // Repeater container; DO NOT push path frame
            traverse(child.id);
            continue;
         }

         if (isRepeaterGroup(child)) {
            // group.response.val stores rowKey (datasource business key)
            const rowKey = Array.isArray(child.response) ? child.response[0]?.val : child.response?.val;

            // Datasource/list groups carry a business key. Free-flow groups may not;
            // preserve those using the persisted submission-group item identity.
            const hasBusinessKey = rowKey !== null && rowKey !== undefined && rowKey !== '';
            const persistedGroupId = child.id ?? null;
            if (persistedGroupId == null) {
               projected.invalidRepeaterGroups.push({ idSubmissionItem: null, idFormItem: child.idFormItem ?? null });
               // A missing item ID cannot have safely traversable children.
               continue;
            }
            if (!hasBusinessKey && !includeGroupsWithoutRowKey) {
               // Preserve the historic Google Sheets projection contract by default.
               traverse(child.id);
               continue;
            }

            const frame = {
               repeaterFormItem: child.idFormItem, // historical repeater definition
               repeaterGroupNo: child.no ?? null, // ordinal within its parent
               rowKey: hasBusinessKey ? rowKey : null, // optional datasource business key
               ...(hasBusinessKey ? {} : { idSubmissionGroup: persistedGroupId }),
               groupLabel: child.label ?? null // persisted label where available
            };

            pathStack.push(frame);
            projected.groupPaths.push(pathStack.slice());
            traverse(child.id);
            pathStack.pop();
            continue;
         }

         // Leaf / normal item
         if (shouldEmitFact(child, includeNulls)) emitFact(child);

         // Defensive: traverse any descendants (rare but safe)
         traverse(child.id);
      }
   }

   // Start at root (idParent === null)
   traverse(null);

   // Derive UI nodes from facts
   projected.nodes = buildNodesFromFacts(projected);

   return projected;
}

/**
 * Build a hierarchical UI-oriented nodes tree from facts grouped by path frames.
 *
 * Output shape:
 * [
 *   {
 *     type: 'submission',
 *     meta,
 *     fields: [...],
 *     groups: [
 *       { type: 'repeaterGroup', frame, fields: [...], groups: [...] }
 *     ]
 *   }
 * ]
 */
function buildNodesFromFacts(projected) {
   const root = {
      type: 'submission',
      meta: projected.meta,
      fields: [],
      groups: []
   };

   // Root-level facts
   const rootFacts = projected.facts.filter((f) => (f.path ?? []).length === 0);
   root.fields = rootFacts.map(toNodeField);

   // Build nested groups keyed by full path
   const groupIndex = new Map(); // pathKey -> group node

   function ensureGroup(pathFrames) {
      const pathKey = JSON.stringify(pathFrames);
      if (groupIndex.has(pathKey)) return groupIndex.get(pathKey);

      const frame = pathFrames[pathFrames.length - 1];
      const node = {
         type: 'repeaterGroup',
         frame,
         fields: [],
         groups: []
      };

      groupIndex.set(pathKey, node);

      if (pathFrames.length === 1) {
         root.groups.push(node);
      } else {
         const parent = ensureGroup(pathFrames.slice(0, -1));
         parent.groups.push(node);
      }

      return node;
   }

   for (const f of projected.facts) {
      const p = f.path ?? [];
      if (p.length === 0) continue;
      const g = ensureGroup(p);
      g.fields.push(toNodeField(f));
   }

   // Optional stable ordering
   function sortGroup(node) {
      node.fields.sort((a, b) => String(a.label ?? '').localeCompare(String(b.label ?? '')));
      node.groups.sort((a, b) => (a.frame?.repeaterGroupNo ?? 0) - (b.frame?.repeaterGroupNo ?? 0));
      for (const c of node.groups) sortGroup(c);
   }
   sortGroup(root);

   return [root];
}

function toNodeField(fact) {
   return {
      key: fact.key,
      itemType: fact.itemType,
      label: fact.label,
      value: fact.value,
      upd: fact.upd
   };
}

/**
 * Helpers for export flattening
 */

function normalizeOptionsValue(value) {
   if (value == null) return [];
   return Array.isArray(value) ? value : [value];
}

function writeOptionsToRow(row, label, value) {
   const opts = normalizeOptionsValue(value);

   // If it was single-select (scalar object), keep classic "(id)/(txt)" columns
   if (value != null && !Array.isArray(value)) {
      row[`${label} (id)`] = opts[0]?.id ?? null;
      row[`${label} (txt)`] = opts[0]?.txt ?? null;
      return;
   }

   // Multi-select: joined + lossless JSON
   const ids = opts.map((o) => o?.id).filter((x) => x != null);
   const txts = opts.map((o) => o?.txt).filter((x) => x != null);

   row[`${label} (ids)`] = ids.length ? ids.join(';') : null;
   row[`${label} (txts)`] = txts.length ? txts.join(';') : null;
   row[`${label} (json)`] = opts.length ? JSON.stringify(opts) : null;
}

function writePhotosToRow(row, label, photosArray) {
   const photos = Array.isArray(photosArray) ? photosArray : photosArray ? [photosArray] : [];

   const localUris = photos.map((p) => (p && typeof p === 'object' ? p.localUri : null)).filter((x) => x != null);

   const remoteUris = photos.map((p) => (p && typeof p === 'object' ? p.remoteUri : null)).filter((x) => x != null);

   row[`${label} (remoteUris)`] = remoteUris.length ? remoteUris.join(';') : null;
   row[`${label} (localUris)`] = localUris.length ? localUris.join(';') : null;

   // Lossless (recommended)
   row[`${label} (json)`] = photos.length ? JSON.stringify(photos) : null;
}

/**
 * Export style A: Relational tables
 * - Submissions: one row per submission (facts where path.length === 0)
 * - Repeater_<formItemId>: one row per repeater group (grouped by final path frame)
 */
function buildRelationalExportTables(projected, { includePathIdentity = true, includeGroupRows = false } = {}) {
   const meta = projected.meta;

   const submissionsRow = {
      noSubmission: meta.noSubmission,
      noForm: meta.noForm,
      noLocation: meta.noLocation,
      ctd: meta.ctd,
      upd: meta.upd
   };

   // Root facts -> main sheet
   for (const f of projected.facts.filter((x) => (x.path ?? []).length === 0)) {
      if (f.itemType === ItemTypes.OPTIONS) {
         writeOptionsToRow(submissionsRow, f.label, f.value);
      } else if (f.itemType === ItemTypes.PHOTO) {
         writePhotosToRow(submissionsRow, f.label, f.value);
      } else {
         submissionsRow[f.label] = f.value ?? null;
      }
   }

   // Repeater sheets
   const sheets = new Map(); // sheetName -> rows[]
   for (const id of projected.repeaterFormItems ?? []) {
      sheets.set(`Repeater_${id}`, []);
   }

   const repeatedFacts = projected.facts.filter((x) => (x.path ?? []).length > 0);
   const groupsAsFacts = includeGroupRows
      ? (projected.groupPaths ?? []).map((path) => ({ path, __groupOnly: true })) : [];
   for (const f of [...groupsAsFacts, ...repeatedFacts]) {
      const lastFrame = f.path[f.path.length - 1];
      const sheetName = `Repeater_${lastFrame.repeaterFormItem}`;
      if (!sheets.has(sheetName)) sheets.set(sheetName, []);

      const identity = {
         noSubmission: meta.noSubmission,
         path: f.path.map(({ repeaterFormItem, repeaterGroupNo, rowKey, idSubmissionGroup }) => ({
            repeaterFormItem,
            repeaterGroupNo,
            rowKey,
            ...(idSubmissionGroup != null ? { idSubmissionGroup } : {})
         }))
      };
      const parentIdentity = f.path.length > 1
         ? JSON.stringify({ noSubmission: meta.noSubmission, path: identity.path.slice(0, -1) })
         : null;
      const rowKey = JSON.stringify(identity);

      let row = sheets.get(sheetName).find((r) => r.__rowKey === rowKey);
      if (!row) {
         row = {
            __rowKey: rowKey,
            noSubmission: meta.noSubmission,
            noForm: meta.noForm,
            noLocation: meta.noLocation,

            repeaterFormItem: lastFrame.repeaterFormItem,
            repeaterGroupNo: lastFrame.repeaterGroupNo,
            rowKey: lastFrame.rowKey,
            ...(includePathIdentity ? {
               pathIdentity: rowKey,
               parentPathIdentity: parentIdentity,
               submissionGroupId: lastFrame.idSubmissionGroup ?? null
            } : {}),
            groupLabel: lastFrame.groupLabel ?? null
         };
         sheets.get(sheetName).push(row);
      }

      if (f.__groupOnly) continue;
      if (f.itemType === ItemTypes.OPTIONS) {
         writeOptionsToRow(row, f.label, f.value);
      } else if (f.itemType === ItemTypes.PHOTO) {
         writePhotosToRow(row, f.label, f.value);
      } else {
         row[f.label] = f.value ?? null;
      }
   }

   // Cleanup internal keys
   const out = { Submissions: [submissionsRow] };
   for (const [name, rows] of sheets.entries()) {
      out[name] = rows.map((r) => {
         const c = { ...r };
         delete c.__rowKey;
         return c;
      });
   }

   return out;
}

/**
 * Export style B: Exploded (master repeated)
 * One row per unique full path (deepest group).
 * Row includes:
 * - submission meta
 * - all root facts
 * - repeater ancestry context frames
 * - facts belonging to that path
 */
function buildExplodedRows(projected, { includeParentWhenEmpty = true, includeGroupRows = false } = {}) {
   const meta = projected.meta;

   const rootFacts = projected.facts.filter((f) => (f.path ?? []).length === 0);

   const grouped = _.groupBy(
      projected.facts.filter((f) => (f.path ?? []).length > 0),
      (f) => JSON.stringify(f.path)
   );
   if (includeGroupRows) {
      for (const path of projected.groupPaths ?? []) {
         const pathKey = JSON.stringify(path);
         if (!Object.hasOwn(grouped, pathKey)) grouped[pathKey] = [];
      }
   }

   const rows = [];

   for (const [pathKey, facts] of Object.entries(grouped)) {
      const path = JSON.parse(pathKey);
      const lastFrame = path[path.length - 1];

      const row = {
         noSubmission: meta.noSubmission,
         noForm: meta.noForm,
         noLocation: meta.noLocation,
         ctd: meta.ctd,
         upd: meta.upd,

         repeaterFormItem: lastFrame.repeaterFormItem,
         repeaterGroupNo: lastFrame.repeaterGroupNo,
         rowKey: lastFrame.rowKey,
         ...(includeGroupRows ? { submissionGroupId: lastFrame.idSubmissionGroup ?? null } : {}),
         groupLabel: lastFrame.groupLabel ?? null
      };

      // Include ancestry frames (indexed)
      path.forEach((fr, idx) => {
         const pfx = `p${idx + 1}`;
         row[`${pfx}_repeaterFormItem`] = fr.repeaterFormItem ?? null;
         row[`${pfx}_repeaterGroupNo`] = fr.repeaterGroupNo ?? null;
         row[`${pfx}_rowKey`] = fr.rowKey ?? null;
         row[`${pfx}_groupLabel`] = fr.groupLabel ?? null;
      });

      // Root facts into every row
      for (const f of rootFacts) {
         if (f.itemType === ItemTypes.OPTIONS) {
            writeOptionsToRow(row, f.label, f.value);
         } else if (f.itemType === ItemTypes.PHOTO) {
            writePhotosToRow(row, f.label, f.value);
         } else {
            row[f.label] = f.value ?? null;
         }
      }

      // Path facts
      for (const f of facts) {
         if (f.itemType === ItemTypes.OPTIONS) {
            writeOptionsToRow(row, f.label, f.value);
         } else if (f.itemType === ItemTypes.PHOTO) {
            writePhotosToRow(row, f.label, f.value);
         } else {
            row[f.label] = f.value ?? null;
         }
      }

      rows.push(row);
   }

   if (includeParentWhenEmpty && rows.length === 0) {
      // Preserve the parent submission when no repeater facts were emitted.
      rows.push(buildRelationalExportTables({ ...projected, repeaterFormItems: [] }).Submissions[0]);
   }

   rows.sort((a, b) => {
      const af = String(a.repeaterFormItem ?? '');
      const bf = String(b.repeaterFormItem ?? '');
      if (af !== bf) return af.localeCompare(bf);
      return (a.repeaterGroupNo ?? 0) - (b.repeaterGroupNo ?? 0);
   });

   return rows;
}

function formatFactForDisplay(fact) {
   if (!fact) return null;

   const { itemType, value } = fact;

   if (value == null) {
      return {
         type: 'empty',
         text: ''
      };
   }

   // TODO (boolean)
   if (itemType === ItemTypes.TODO) {
      return {
         type: 'boolean',
         text: value ? 'Yes' : 'No',
         raw: value
      };
   }

   // OPTIONS
   if (itemType === ItemTypes.OPTIONS) {
      const arr = Array.isArray(value) ? value : [value];

      const labels = arr.map((o) => o?.txt ?? o?.id ?? '').filter(Boolean);

      return {
         type: 'options',
         text: labels.join(', '),
         items: arr
      };
   }

   // PHOTO
   if (itemType === ItemTypes.PHOTO) {
      const arr = Array.isArray(value) ? value : [value];

      return {
         type: 'photo',
         text: `${arr.length} photo${arr.length === 1 ? '' : 's'}`,
         items: arr // contains localUri / remoteUri
      };
   }

   // DATETIME
   if (itemType === ItemTypes.DATETIME) {
      const date = new Date(value);
      return {
         type: 'datetime',
         text: isNaN(date.getTime()) ? String(value) : date.toLocaleString(),
         raw: value
      };
   }

   // NUMBER
   if (itemType === ItemTypes.NUMBER) {
      return {
         type: 'number',
         text: String(value),
         raw: value
      };
   }

   // CALCULATED
   if (itemType === ItemTypes.CALCULATED) {
      return {
         type: 'calculated',
         text: String(value),
         raw: value
      };
   }

   // TEXT + fallback
   return {
      type: 'text',
      text: String(value),
      raw: value
   };
}

function formatFactForCsv(fact) {
   if (!fact || fact.value == null) return '';

   const { itemType, value } = fact;

   if (itemType === ItemTypes.TODO) {
      return value ? 'Yes' : 'No';
   }

   if (itemType === ItemTypes.OPTIONS) {
      const arr = Array.isArray(value) ? value : [value];
      return arr
         .map((o) => o?.txt ?? o?.id ?? '')
         .filter(Boolean)
         .join('; ');
   }

   if (itemType === ItemTypes.PHOTO) {
      const arr = Array.isArray(value) ? value : [value];
      return arr
         .map((p) => p?.remoteUri ?? '')
         .filter(Boolean)
         .join('; ');
   }

   if (itemType === ItemTypes.DATETIME) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toISOString();
   }

   return String(value);
}

function formatFactForSheetCell(fact) {
   if (!fact || fact.value == null) return '';

   const { itemType, value } = fact;

   if (itemType === ItemTypes.TODO) return value ? 'Yes' : 'No';

   if (itemType === ItemTypes.OPTIONS) {
      const arr = Array.isArray(value) ? value : [value];
      return arr
         .map((o) => o?.txt ?? o?.id ?? '')
         .filter(Boolean)
         .join('; ');
   }

   if (itemType === ItemTypes.PHOTO) {
      const arr = Array.isArray(value) ? value : [value];
      // Best default for a sheet: remote urls
      return arr
         .map((p) => p?.remoteUri ?? '')
         .filter(Boolean)
         .join('; ');
   }

   if (itemType === ItemTypes.DATETIME) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toISOString();
   }

   return value;
}

module.exports = {
   buildProjectedSubmission,
   buildNodesFromFacts,

   buildRelationalExportTables,
   buildExplodedRows,

   formatFactForDisplay,
   formatFactForSheetCell,
   formatFactForCsv,

   // exported mainly for tests
   parsePersistedValue,
   getResponseUpd
};
